/**
 * **화면 체류·스크롤 측정** — 측정 대상 화면(`ENGAGEMENT_PAGES`)의 진입 하나마다 `page_engaged` 누적값을 보낸다.
 *
 * 보내는 때: 숨겨질 때 · 떠날 때(`pagehide`) · 다른 경로로 갈 때 · 보인 시간이 박자(`ENGAGEMENT_HEARTBEAT_MS`)를 넘을 때.
 * 숨김·떠남은 큐를 기다리지 않고 `sendBeacon` 으로 흘려보낸다 — ⚠️ 비콘에는 토큰이 없어 로그인 사용자도 그 줄은 `user_id` 가 NULL 이다(`anonymous_id` 로 잇는다).
 * 값이 그대로면 다시 보내지 않는다(숨김 직후의 `pagehide` 가 같은 줄을 두 번 만들지 않게). 한 진입의 값은 `view_id` 별 MAX 로 읽는다.
 */

import type { AnalyticsPage } from 'src/types/analytics/events';

import { ENGAGEMENT_PAGES, ENGAGEMENT_SECTION_LINE, ENGAGEMENT_HEARTBEAT_MS } from 'src/constants/analytics-engagement';

import { newId } from './session';
import { pageOfPath } from './page-of-path';
import { track, flushAnalyticsOnUnload } from './client';

type Trigger = 'hidden' | 'pagehide' | 'route_change' | 'heartbeat';

/** 문서 스크롤 → 본 깊이 % (0~100 정수). 문서가 화면보다 짧으면 100 */
export function scrollPercent(scrollY: number, viewportHeight: number, scrollHeight: number): number {
  if (scrollHeight <= viewportHeight || scrollHeight <= 0) return 100;
  const pct = Math.round(((scrollY + viewportHeight) / scrollHeight) * 100);
  return Math.min(100, Math.max(0, pct));
}

/**
 * 구역 윗변(화면 기준 px)들 → 기준선을 넘은 가장 아래 구역의 차례.
 * @returns 아무 구역도 못 넘었으면 -1
 */
export function deepestSectionIndex(tops: readonly number[], viewportHeight: number): number {
  const line = viewportHeight * ENGAGEMENT_SECTION_LINE;
  let deepest = -1;
  tops.forEach((top, i) => {
    if (top <= line) deepest = i;
  });
  return deepest;
}

/** Provider 가 경로가 바뀔 때마다 부른다 — 같은 경로면 아무것도 안 한다 */
export function startPageEngagement(path: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (current?.path === path) return;
  installListeners();
  if (current) {
    current.emit('route_change');
    current.stop();
    current = null;
  }
  const page = pageOfPath(path);
  if (page && (ENGAGEMENT_PAGES as readonly AnalyticsPage[]).includes(page)) current = new EngagementView(path);
}

/** 테스트용 — 상태와 듣기를 처음으로 */
export function resetPageEngagementForTests(): void {
  current?.stop();
  current = null;
}

// ---- private ------------------------------------------------------------

let current: EngagementView | null = null;
let listening = false;

function installListeners(): void {
  if (listening) return;
  listening = true;
  document.addEventListener('visibilitychange', () => {
    if (!current) return;
    if (document.visibilityState === 'hidden') current.hide();
    else current.show();
  });
  window.addEventListener('pagehide', () => current?.emit('pagehide'));
  let pending = false;
  window.addEventListener(
    'scroll',
    () => {
      if (pending || !current) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        current?.measure();
      });
    },
    { passive: true }
  );
}

class EngagementView {
  readonly path: string;

  private readonly viewId = newId();

  private visibleMs = 0;

  private visibleSince: number | null;

  private maxScrollPct = 0;

  private deepestIndex = -1;

  private deepestId: string | null = null;

  private seq = 0;

  private lastSent = '';

  private beat = 0;

  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(path: string) {
    this.path = path;
    this.visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
    window.requestAnimationFrame(() => this.measure());
    this.schedule();
  }

  measure(): void {
    const doc = document.documentElement;
    this.maxScrollPct = Math.max(this.maxScrollPct, scrollPercent(window.scrollY, window.innerHeight, doc.scrollHeight));
    // 그려지지 않은 사본(`display:none`)은 윗변이 0 이라 「다 봤다」로 읽힌다 — 상자가 있는 것만 센다
    const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-section-id]')).filter((el) => el.getClientRects().length > 0);
    const index = deepestSectionIndex(
      sections.map((el) => el.getBoundingClientRect().top),
      window.innerHeight
    );
    if (index > this.deepestIndex) {
      this.deepestIndex = index;
      this.deepestId = sections[index]?.dataset.sectionId?.slice(0, 64) ?? null;
    }
  }

  hide(): void {
    this.pause();
    this.emit('hidden');
  }

  show(): void {
    if (this.visibleSince === null) this.visibleSince = Date.now();
    this.schedule();
  }

  stop(): void {
    this.pause();
  }

  emit(trigger: Trigger): void {
    // 경로가 바뀐 뒤에는 DOM 이 이미 새 화면이다 — 직전까지 잰 값으로 마감한다
    if (trigger !== 'route_change') this.measure();
    const visibleMs = this.elapsed();
    const key = `${Math.round(visibleMs / 1000)}|${this.maxScrollPct}|${this.deepestId}`;
    if (key === this.lastSent && trigger !== 'heartbeat') return;
    this.lastSent = key;
    this.seq += 1;
    track({
      name: 'page_engaged',
      type: 'page',
      props: {
        view_id: this.viewId,
        visible_ms: Math.round(visibleMs),
        max_scroll_pct: this.maxScrollPct,
        deepest_section: this.deepestId,
        trigger,
        seq: this.seq,
      },
    });
    if (trigger === 'hidden' || trigger === 'pagehide') flushAnalyticsOnUnload();
  }

  private elapsed(): number {
    return this.visibleMs + (this.visibleSince === null ? 0 : Date.now() - this.visibleSince);
  }

  private pause(): void {
    if (this.visibleSince !== null) {
      this.visibleMs += Date.now() - this.visibleSince;
      this.visibleSince = null;
    }
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** 다음 박자까지 남은 «보이는» 시간만큼 기다린다 — 숨겨지면 `pause` 가 끊는다 */
  private schedule(): void {
    if (this.timer || this.visibleSince === null || this.beat >= ENGAGEMENT_HEARTBEAT_MS.length) return;
    const wait = Math.max(0, ENGAGEMENT_HEARTBEAT_MS[this.beat] - this.elapsed());
    this.timer = setTimeout(() => {
      this.timer = null;
      this.beat += 1;
      this.emit('heartbeat');
      this.schedule();
    }, wait);
  }
}
